/**
 * DatasetFilters - Text and property filtering for dataset list
 * @module components/starnexus/datasets/DatasetFilters
 *
 */

import React from 'react';
import TextFilter from '@amzn/awsui-components-console/text-filter';
import PropertyFilter from '@amzn/awsui-components-console/property-filter';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import type { PropertyFilterToken } from '../../../hooks/starnexus/useDatasets';

// Property filter definitions
const FILTERING_PROPERTIES = [
  {
    key: 'teamName',
    propertyLabel: 'Team',
    groupValuesLabel: 'Team values',
    operators: ['=', '!='] as const
  },
  {
    key: 'status',
    propertyLabel: 'Status',
    groupValuesLabel: 'Status values',
    operators: ['=', '!='] as const
  },
  {
    key: 'processingType',
    propertyLabel: 'Processing type',
    groupValuesLabel: 'Processing type values',
    operators: ['=', '!='] as const
  },
  {
    key: 'successRate',
    propertyLabel: 'Success rate',
    groupValuesLabel: 'Success rate values',
    operators: ['>=', '<=', '>', '<'] as const
  }
];

// Filter options for dropdowns
const FILTER_OPTIONS = {
  status: [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'error', label: 'Error' },
    { value: 'processing', label: 'Processing' }
  ],
  processingType: [
    { value: 'csv-to-json', label: 'CSV to JSON' },
    { value: 'json-passthrough', label: 'JSON Passthrough' },
    { value: 'excel-to-json', label: 'Excel to JSON' }
  ],
  teamName: [
    { value: 'team-001', label: 'Retail Analytics' },
    { value: 'team-002', label: 'Customer Experience' },
    { value: 'team-003', label: 'Operations' },
    { value: 'team-004', label: 'Marketing' },
    { value: 'team-005', label: 'Finance' },
    { value: 'team-006', label: 'Human Resources' }
  ]
};

interface DatasetFiltersProps {
  filterText: string;
  onFilterTextChange: (text: string) => void;
  propertyFilters: PropertyFilterToken[];
  onPropertyFiltersChange: (tokens: PropertyFilterToken[]) => void;
  loading?: boolean;
  totalCount: number;
}

export const DatasetFilters: React.FC<DatasetFiltersProps> = ({
  filterText,
  onFilterTextChange,
  propertyFilters,
  onPropertyFiltersChange,
  loading = false,
  totalCount
}) => {
  return (
    <SpaceBetween size="s" direction="horizontal">
      <TextFilter
        filteringText={filterText}
        filteringPlaceholder="Search datasets"
        onChange={({ detail }) => onFilterTextChange(detail.filteringText)}
        countText={`${totalCount} matches`}
        disabled={loading}
      />
      <PropertyFilter
        query={{ tokens: propertyFilters as any, operation: 'and' }}
        onChange={({ detail }) =>
          onPropertyFiltersChange(detail.tokens as PropertyFilterToken[])
        }
        filteringProperties={FILTERING_PROPERTIES}
        filteringOptions={[
          ...FILTER_OPTIONS.status.map((o) => ({
            propertyKey: 'status',
            value: o.value,
            label: o.label
          })),
          ...FILTER_OPTIONS.processingType.map((o) => ({
            propertyKey: 'processingType',
            value: o.value,
            label: o.label
          })),
          ...FILTER_OPTIONS.teamName.map((o) => ({
            propertyKey: 'teamName',
            value: o.value,
            label: o.label
          }))
        ]}
        filteringPlaceholder="Filter by property"
        disabled={loading}
        i18nStrings={{
          filteringAriaLabel: 'Filter datasets',
          dismissAriaLabel: 'Dismiss',
          filteringPlaceholder: 'Filter by property',
          groupValuesText: 'Values',
          groupPropertiesText: 'Properties',
          operatorsText: 'Operators',
          operationAndText: 'and',
          operationOrText: 'or',
          operatorLessText: 'Less than',
          operatorLessOrEqualText: 'Less than or equal',
          operatorGreaterText: 'Greater than',
          operatorGreaterOrEqualText: 'Greater than or equal',
          operatorContainsText: 'Contains',
          operatorDoesNotContainText: 'Does not contain',
          operatorEqualsText: 'Equals',
          operatorDoesNotEqualText: 'Does not equal',
          editTokenHeader: 'Edit filter',
          propertyText: 'Property',
          operatorText: 'Operator',
          valueText: 'Value',
          cancelActionText: 'Cancel',
          applyActionText: 'Apply',
          allPropertiesLabel: 'All properties',
          tokenLimitShowMore: 'Show more',
          tokenLimitShowFewer: 'Show fewer',
          clearFiltersText: 'Clear filters',
          removeTokenButtonAriaLabel: (token) =>
            `Remove filter: ${token.propertyKey} ${token.operator} ${token.value}`,
          enteredTextLabel: (text) => `Use: "${text}"`
        }}
      />
    </SpaceBetween>
  );
};

export default DatasetFilters;
